import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import TooltipWrapper, {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import usePnlType from '@/features/portfolio/hooks/use-pnl-type'; // eslint-disable-line import/no-restricted-paths
import useFundedAccount from '@/hooks/use-funded-account';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { cn } from '@/utils/cn';
import { bigIntToNum, formatNumber } from '@/utils/value-format';

import { useFetchAccount } from '../api/get-account';
import { useCapitalAccountData } from '../utils/hooks';

import CapitalIndicators from './capital-indicators';
import HealthBar from './health-bar';
import HealthCircle from './health-circle';

export default function AccountStats() {
  const { t } = useTranslation();
  const { isFundedOrEval, isEval } = useFundedAccount();

  const capitalStatus = useAccountStore((state) =>
    state.getCapitalStatus(state.address),
  );

  const { isLoading } = useFetchAccount();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center border-b border-border bg-background py-8">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary"></div>
            <div className="absolute inset-0 h-8 w-8 animate-ping rounded-full border border-primary opacity-20"></div>
          </div>
          <p className="font-mono text-sm text-vestgrey-100">
            {t('accountStats.loadingAccount')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col border-b border-border py-3">
      {!isFundedOrEval && <HealthPct />}
      <div className="flex h-10 w-full items-center justify-between px-4 py-3 text-vestgrey-100">
        <div className="flex items-center gap-2">
          <span>{t('accountStats.accountValue')}</span>
        </div>
        <div className="flex items-center gap-3">
          {isFundedOrEval && (
            <span
              className={cn(
                'rounded-sm px-2 py-0.5 font-mono text-xs uppercase tracking-wide text-black',
                isEval ? 'bg-yellow' : 'bg-green',
              )}
            >
              {isEval ? t('accountStats.eval') : t('accountStats.funded')}
            </span>
          )}
          <p className="font-mono text-vestgrey-50">
            <Value />
          </p>
        </div>
      </div>
      {!isFundedOrEval && (
        <div className="flex h-10 w-full items-center justify-between px-4 py-3 text-vestgrey-100">
          <p>{t('accountStats.maintenanceMargin')}</p>
          <p className="font-mono text-vestgrey-50">
            <MaintMarginReq />
          </p>
        </div>
      )}
      <div className="flex h-10 w-full items-center justify-between px-4 py-3 text-vestgrey-100">
        <p>{t('accountStats.leverage')}</p>
        <p className="font-mono text-vestgrey-50">
          <Leverage />
        </p>
      </div>
      <div className="flex h-10 w-full items-center justify-between px-4 py-3 text-vestgrey-100">
        <p>{t('accountStats.unrealizedPnl')}</p>
        <UnrealizedPnL />
      </div>
      {isFundedOrEval && (
        <CapitalIndicators capitalStatus={capitalStatus} hideGoal={!isEval} />
      )}
    </div>
  );
}

export function AccountHealth() {
  const { t } = useTranslation();

  const { isFundedOrEval } = useFundedAccount();
  const healthPct = useCurrentAccount((state) =>
    state.healthPct ? bigIntToNum(state.healthPct.bigint, 2) : undefined,
  );

  return (
    <>
      <div className="mb-8 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="select-none text-2xl font-medium capitalize md:text-3xl">
              {t('accountStats.accountHealth')}
            </div>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <Info size={14} />
                </TooltipTrigger>
                <TooltipContent>
                  {t('accountStats.accountHealthTooltip')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <span
            className={cn('text-xl font-medium', {
              'text-green': healthPct !== undefined && healthPct >= 60,
              'text-yellow':
                healthPct !== undefined && healthPct > 20 && healthPct < 60,
              'text-red': healthPct !== undefined && healthPct <= 20,
              'text-muted-foreground':
                healthPct === undefined || healthPct === 0,
            })}
          >
            {formatNumber(healthPct, { digits: 0, style: 'percent' })}
          </span>
        </div>
        <HealthBar value={healthPct} />
      </div>
      <div className="flex items-center justify-between text-xl">
        <div className="text-vestgrey-100">
          {t('accountStats.accountValue')}
        </div>
        <Value />
      </div>
      {!isFundedOrEval && (
        <div className="flex items-center justify-between text-xl">
          <div className="text-vestgrey-100">
            {t('accountStats.maintenanceMargin')}
          </div>
          <MaintMarginReq />
        </div>
      )}
      <div className="flex items-center justify-between text-xl">
        <div className="text-vestgrey-100">{t('accountStats.leverage')}</div>
        <Leverage />
      </div>
      <div className="flex items-center justify-between text-xl">
        <div className="text-vestgrey-100">
          {t('accountStats.unrealizedPnl')}
        </div>
        <UnrealizedPnL />
      </div>
    </>
  );
}

export const AccountHealthCircle = () => {
  const { isEval, accountSize, goalSize } = useCapitalAccountData();
  const { value, healthPct } = useCurrentAccount((state) => ({
    value: +state.value.decimal,
    healthPct: state.healthPct
      ? bigIntToNum(state.healthPct.bigint, 2)
      : undefined,
  }));

  return (
    <HealthCircle
      value={
        isEval ? ((value - accountSize) / (goalSize || 1)) * 100 : healthPct
      }
      strokeWidth={5}
      color={isEval ? 'hsl(var(--green))' : undefined}
    />
  );
};

function Leverage() {
  const leverage = useCurrentAccount((state) => state.leverage.decimal);
  return formatNumber(leverage, { style: 'leverage' });
}

function UnrealizedPnL() {
  const type = usePnlType();

  const { bigint, decimal } = useCurrentAccount(
    (state) => state.unrealizedPnl[type],
  );

  return (
    <span
      className={cn({
        'text-green': bigint > 0n,
        'text-red': bigint < 0n,
        'text-muted-foreground': bigint === 0n,
      })}
    >
      {formatNumber(decimal, {
        digits: 2,
        style: 'currency',
        showChange: true,
      })}
    </span>
  );
}

function HealthPct() {
  const { t } = useTranslation();
  const healthPct = useCurrentAccount((state) =>
    state.healthPct ? bigIntToNum(state.healthPct.bigint, 2) : undefined,
  );
  return (
    <div className="flex w-full flex-col justify-between gap-2 px-4 py-3 text-vestgrey-100">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <TooltipWrapper
            direction="bottom"
            align="left"
            content={t('accountStats.accountHealthTooltip')}
          >
            <h5 className="select-none text-vestgrey-100 underline">
              {t('accountStats.accountHealth')}
            </h5>
          </TooltipWrapper>
        </div>

        <span
          className={cn({
            'text-green': healthPct !== undefined && healthPct >= 60,
            'text-yellow':
              healthPct !== undefined && healthPct > 20 && healthPct < 60,
            'text-red': healthPct !== undefined && healthPct <= 20,
            'text-muted-foreground': healthPct === undefined || healthPct === 0,
          })}
        >
          {formatNumber(healthPct, { digits: 0, style: 'percent' })}
        </span>
      </div>
      <HealthBar value={healthPct} />
    </div>
  );
}

function Value() {
  const value = useCurrentAccount((state) => state.value.decimal);
  return formatNumber(value, { digits: 2, style: 'currency' });
}

function MaintMarginReq() {
  const maintMarginReq = useCurrentAccount(
    (state) => state.maintMarginReq.decimal,
  );
  return formatNumber(maintMarginReq, { digits: 2 });
}
